import { useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { AllowedAction } from '@/data/allowedActions'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// 來源：.claude/skills/api-permission/SKILL.md §11
// 因 data-access 為 CJS、前端為 ESM，前端保留本地副本
const MODULE_LABELS: Record<string, string> = {
  // System (sys_admin)
  system_user: '使用者管理',
  system_user_role: '角色管理',
  system_clinic: '診所管理',
  system_provider: 'Provider 管理',
  system_system_version: '系統版本',
  system_ad_supplier: '廣告供應商',
  system_integration_vendor: '整合廠商',
  system_audit_log: '操作日誌',
  // Provider
  provider_user_management: '用戶管理',
  provider_user_role: '角色管理',
  provider_clinic_management: '診所管理',
  provider_supplier_management: '供應商管理',
  provider_provider_settings: 'Provider 設定',
  provider_ad_schedule: '廣告排程',
  provider_broadcast_device: '廣播設備',
  provider_audit_log: '操作日誌',
  provider_war_room: '戰情室',
  provider_toolkit: '工具包',
  provider_clinic_group_management: '診所集團管理',
  provider_ad_supplier_group_management: '投資方集團管理',
  // Clinic
  clinic_user_management: '使用者管理',
  clinic_user_role: '角色管理',
  clinic_clinic_management: '診所管理',
  clinic_queue_system: '叫號系統',
  clinic_appointment_system: '預約系統',
  clinic_broadcast_device: '廣播設備',
  clinic_queue_machine_management: '叫號機管理',
  clinic_queue_machine_operation: '叫號機操作',
  // AdSupplier
  ad_supplier_user_management: '使用者管理',
  ad_supplier_user_role: '角色管理',
  ad_supplier_supplier_management: '供應商管理',
  ad_supplier_ad_management: '廣告管理',
  ad_supplier_statistics_report: '統計報表',
  ad_supplier_clinic_statistics: '診所統計',
  // ClinicGroup
  clinic_group_user_management: '使用者管理',
  clinic_group_user_role: '角色管理',
  clinic_group_clinic_management: '診所管理',
  clinic_group_group_settings: '集團設定',
  clinic_group_broadcast_device: '廣播設備',
  clinic_group_audit_log: '操作日誌',
  // AdSupplierGroup
  ad_supplier_group_user_management: '使用者管理',
  ad_supplier_group_user_role: '角色管理',
  ad_supplier_group_supplier_management: '供應商管理',
  ad_supplier_group_group_settings: '集團設定',
  ad_supplier_group_ad_management: '廣告管理',
  ad_supplier_group_statistics_report: '統計報表',
}

// 順序也決定畫面顯示順序
const SCOPES: { prefix: string; label: string }[] = [
  { prefix: 'system_', label: '系統管理員' },
  { prefix: 'provider_', label: 'Provider' },
  { prefix: 'clinic_group_', label: '診所集團' },
  { prefix: 'clinic_', label: '診所' },
  { prefix: 'ad_supplier_group_', label: '投資方集團' },
  { prefix: 'ad_supplier_', label: '廣告供應商' },
]

const ACTION_LABELS: Record<AllowedAction, string> = {
  [AllowedAction.C]: '新增',
  [AllowedAction.R]: '讀取',
  [AllowedAction.U]: '更新',
  [AllowedAction.D]: '刪除',
}

const ACTION_ORDER: AllowedAction[] = [
  AllowedAction.C,
  AllowedAction.R,
  AllowedAction.U,
  AllowedAction.D,
]

function resolveScope(module: string): { prefix: string; label: string } {
  for (const scope of SCOPES) {
    if (module.startsWith(scope.prefix)) return scope
  }
  return { prefix: '', label: '其他' }
}

export default function PermissionsViewerDialog({ open, onOpenChange }: Props) {
  const { permissions, isLoading } = useMyPermissions()

  const grouped = useMemo(() => {
    const map = new Map<string, { module: string; allowed_action: AllowedAction[] }[]>()
    for (const scope of SCOPES) map.set(scope.label, [])
    for (const p of permissions) {
      const scope = resolveScope(p.module)
      const list = map.get(scope.label) ?? []
      list.push(p)
      map.set(scope.label, list)
    }
    return Array.from(map.entries()).filter(([, items]) => items.length > 0)
  }, [permissions])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>權限瀏覽</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          {isLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
            </div>
          ) : permissions.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              目前沒有任何權限
            </div>
          ) : (
            <div className="space-y-5 py-2">
              {grouped.map(([scopeLabel, items]) => (
                <div key={scopeLabel}>
                  <div className="text-sm font-semibold text-muted-foreground mb-2">
                    {scopeLabel}
                  </div>
                  <div className="rounded-md border divide-y">
                    {items.map((p) => (
                      <div
                        key={p.module}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {MODULE_LABELS[p.module] ?? p.module}
                          </span>
                          <span className="text-xs text-muted-foreground font-mono">
                            {p.module}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {ACTION_ORDER.filter((a) => p.allowed_action.includes(a)).map(
                            (a) => (
                              <Badge key={a} variant="secondary">
                                {ACTION_LABELS[a]}
                              </Badge>
                            ),
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            關閉
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
